import { NgModule } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  UserConsentConnector as CxUserConsentConnector,
  UserConsentService as CxUserConsentService,
  UserEventBuilder as CxUserEventBuilder,
  MODULE_INITIALIZER,
  OccUserConsentAdapter,
  UserConsentAdapter,
  UserEventModule,
} from '@spartacus/core';
import {
  UserProfileConnector as CxUserProfileConnector,
  USER_PROFILE_NORMALIZER,
  USER_PROFILE_SERIALIZER,
} from '@spartacus/user/profile/core';
import { take } from 'rxjs/operators';
import { BaseSiteService } from '../../core/site-context';
import { DebugFacade } from '../../core/user';
import {
  FEED_NORMALIZER,
  ORDER_APPROVAL_DECISION_NORMALIZER,
  PRINCIPAL_CONFIGURATION_NORMALIZER,
  PUNCH_OUT_DATA_NORMALIZER,
  SHOPPING_LIST_NORMALIZER,
  SHOPPING_LIST_PERMISSIONS_SERIALIZER,
} from '../../core/user/connectors/converters';
import { FeedNormalizer } from '../../core/user/connectors/feed.normalizer';
import { OccOrderApprovalDecisionNormalizer } from '../../core/user/connectors/occ-order-approval-decision-normalizer';
import { PrincipalConfigurationNormalizer } from '../../core/user/connectors/principal-configuration-normalizer';
import { PunchOutDataNormalizer } from '../../core/user/connectors/punch-out-data-normalizer';
import {
  PANEL_RESPONSE_NORMALIZER,
  PanelResponseNormalizer,
} from '../../core/user/connectors/recommendations-panel-response-normalizer';
import { PANELS_NORMALIZER, PanelsNormalizer } from '../../core/user/connectors/recommendations-product-panel-normalizer';
import { ShoppingListNormalizer } from '../../core/user/connectors/shopping-list-normalizer';
import { ShoppingListPermissionsSerializer } from '../../core/user/connectors/shopping-list-permissions-serializer';
import {
  UserConsentConnector as PyUserConsentConnector,
  UserConsentConnector,
} from '../../core/user/connectors/user-consent.connector';
import { UserProfileNormalizer } from '../../core/user/connectors/user-profile-normalizer';
import { UserProfileSerializer } from '../../core/user/connectors/user-profile-serializer';
import { UserProfileConnector as PyUserProfileConnector } from '../../core/user/connectors/user-profile.connector';
import { UserEventBuilder as PyUserEventBuilder } from '../../core/user/events';
import { AccountingCodesAdminFacade } from '../../core/user/facades/accounting-codes-admin-facade';
import { AddressFacade } from '../../core/user/facades/address-facade';
import { AnalyticsMetadataFacade } from '../../core/user/facades/analytics-metadata-facade';
import { ArticleInfoAttributesFacade } from '../../core/user/facades/article-info-attributes-facade';
import { AssortmentFacade } from '../../core/user/facades/assortment-facade';
import { CompanyFacade } from '../../core/user/facades/company-facade';
import { DataImportsFacade } from '../../core/user/facades/data-imports-facade';
import { ExperimentalFeaturesFacade } from '../../core/user/facades/experimental-features-facade';
import { FeedFacade } from '../../core/user/facades/feed-facade';
import { OrderApprovalAdminFacade } from '../../core/user/facades/order-approval-admin-facade';
import { OrderApprovalFacade } from '../../core/user/facades/order-approval-facade';
import { OrderCardsAdminFacade } from '../../core/user/facades/order-cards-admin-facade';
import { OrderCardsFacade } from '../../core/user/facades/order-cards-facade';
import { PriceFacade } from '../../core/user/facades/price-facade';
import { PunchOutFacade } from '../../core/user/facades/punch-out-facade';
import { PunchOutStatePersistenceFacade } from '../../core/user/facades/punch-out-state-persistence-facade';
import { RecommendationsFacade } from '../../core/user/facades/recommendations-facade';
import { ShipTosFacade } from '../../core/user/facades/ship-tos-facade';
import { ShoppingListFacade } from '../../core/user/facades/shopping-list-facade';
import { SiteFamilyConfigFacade } from '../../core/user/facades/site-family-config-facade';
import { StockInfoFacade } from '../../core/user/facades/stock-info-facade';
import { UserConsentFacade } from '../../core/user/facades/user-consent-facade';
import { UserFacade } from '../../core/user/facades/user-facade';
import { UserStatePersistenceFacade } from '../../core/user/facades/user-state-persistence-facade';
import {
  AccountingCodesAdminService,
  AddressService,
  AnalyticsMetadataService,
  ArticleInfoAttributesService,
  AssortmentService,
  CompanyService,
  DataImportsService,
  ExperimentalFeaturesService,
  FeedService,
  OrderApprovalAdminService,
  OrderApprovalService,
  OrderCardsAdminService,
  OrderCardsService,
  PriceService,
  PunchOutService,
  RecommendationsService,
  ShipTosService,
  ShoppingListService,
  SiteFamilyConfigService,
  StockInfoService,
  UserConsentWrapperService,
} from '../../core/user/services';
import { DebugService } from '../../core/user/services/debug.service';
import { PunchOutStatePersistenceService } from '../../core/user/services/punch-out-state-persistence.service';
import { UserConsentService as PyUserConsentService } from '../../core/user/services/user-consent.service';
import { UserStatePersistenceService } from '../../core/user/services/user-state-persistence.service';
import { UserService } from '../../core/user/services/user.service';
import { UserModuleLoaded } from '../../core/user/store/actions/user.actions';
import { StateWithUser } from '../../core/user/store/user-state';
import { UserStoreModule } from '../../core/user/store/user-store.module';

export function punchOutStatePersistenceFactory(punchOutStatePersistenceService: PunchOutStatePersistenceFacade): () => void {
  const result = () => punchOutStatePersistenceService.initSync();
  return result;
}
export function userBaseModuleInitFactory(store, baseSiteService: BaseSiteService): () => void {
  const result = () => {
    baseSiteService
      .getActive()
      .pipe(take(1))
      .subscribe(() => {
        store.dispatch(new UserModuleLoaded());
      });
  };
  return result;
}
const facadeProviders = [
  UserService,
  {
    provide: UserFacade,
    useExisting: UserService,
  },
  PyUserConsentService,
  UserConsentWrapperService,
  {
    provide: UserConsentFacade,
    useExisting: UserConsentWrapperService,
  },
  AccountingCodesAdminService,
  {
    provide: AccountingCodesAdminFacade,
    useExisting: AccountingCodesAdminService,
  },
  AddressService,
  {
    provide: AddressFacade,
    useExisting: AddressService,
  },
  AnalyticsMetadataService,
  {
    provide: AnalyticsMetadataFacade,
    useExisting: AnalyticsMetadataService,
  },
  ArticleInfoAttributesService,
  {
    provide: ArticleInfoAttributesFacade,
    useExisting: ArticleInfoAttributesService,
  },
  AssortmentService,
  {
    provide: AssortmentFacade,
    useExisting: AssortmentService,
  },
  CompanyService,
  {
    provide: CompanyFacade,
    useExisting: CompanyService,
  },
  DataImportsService,
  {
    provide: DataImportsFacade,
    useExisting: DataImportsService,
  },
  ExperimentalFeaturesService,
  {
    provide: ExperimentalFeaturesFacade,
    useExisting: ExperimentalFeaturesService,
  },
  FeedService,
  {
    provide: FeedFacade,
    useExisting: FeedService,
  },
  OrderApprovalAdminService,
  {
    provide: OrderApprovalAdminFacade,
    useExisting: OrderApprovalAdminService,
  },
  OrderApprovalService,
  {
    provide: OrderApprovalFacade,
    useExisting: OrderApprovalService,
  },
  OrderCardsAdminService,
  {
    provide: OrderCardsAdminFacade,
    useExisting: OrderCardsAdminService,
  },
  OrderCardsService,
  {
    provide: OrderCardsFacade,
    useExisting: OrderCardsService,
  },
  PriceService,
  {
    provide: PriceFacade,
    useExisting: PriceService,
  },
  PunchOutService,
  {
    provide: PunchOutFacade,
    useExisting: PunchOutService,
  },
  PunchOutStatePersistenceService,
  {
    provide: PunchOutStatePersistenceFacade,
    useExisting: PunchOutStatePersistenceService,
  },
  RecommendationsService,
  {
    provide: RecommendationsFacade,
    useExisting: RecommendationsService,
  },
  ShipTosService,
  {
    provide: ShipTosFacade,
    useExisting: ShipTosService,
  },
  ShoppingListService,
  {
    provide: ShoppingListFacade,
    useExisting: ShoppingListService,
  },
  SiteFamilyConfigService,
  {
    provide: SiteFamilyConfigFacade,
    useExisting: SiteFamilyConfigService,
  },
  StockInfoService,
  {
    provide: StockInfoFacade,
    useExisting: StockInfoService,
  },
  UserStatePersistenceService,
  {
    provide: UserStatePersistenceFacade,
    useExisting: UserStatePersistenceService,
  },
  DebugService,
  {
    provide: DebugFacade,
    useExisting: DebugService,
  },
];

const rootProviders = [
  {
    provide: FEED_NORMALIZER,
    useClass: FeedNormalizer,
    multi: true,
  },
  {
    provide: PUNCH_OUT_DATA_NORMALIZER,
    useClass: PunchOutDataNormalizer,
    multi: true,
  },
  {
    provide: PRINCIPAL_CONFIGURATION_NORMALIZER,
    useClass: PrincipalConfigurationNormalizer,
    multi: true,
  },
  {
    provide: SHOPPING_LIST_NORMALIZER,
    useClass: ShoppingListNormalizer,
    multi: true,
  },
  {
    provide: SHOPPING_LIST_PERMISSIONS_SERIALIZER,
    useClass: ShoppingListPermissionsSerializer,
    multi: true,
  },
  {
    provide: USER_PROFILE_NORMALIZER,
    useClass: UserProfileNormalizer,
    multi: true,
  },
  {
    provide: USER_PROFILE_SERIALIZER,
    useClass: UserProfileSerializer,
    multi: true,
  },
  {
    provide: PANELS_NORMALIZER,
    useClass: PanelsNormalizer,
    multi: true,
  },
  {
    provide: PANEL_RESPONSE_NORMALIZER,
    useClass: PanelResponseNormalizer,
    multi: true,
  },
  {
    provide: ORDER_APPROVAL_DECISION_NORMALIZER,
    useClass: OccOrderApprovalDecisionNormalizer,
    multi: true,
  },
  {
    provide: MODULE_INITIALIZER,
    useFactory: punchOutStatePersistenceFactory,
    deps: [PunchOutStatePersistenceFacade],
    multi: true,
  },
  {
    provide: MODULE_INITIALIZER,
    useFactory: userBaseModuleInitFactory,
    deps: [Store<StateWithUser>, BaseSiteService],
    multi: true,
  },
];

const cxProviders = [
  {
    provide: CxUserEventBuilder,
    useClass: PyUserEventBuilder,
  },
  {
    provide: UserConsentAdapter,
    useClass: OccUserConsentAdapter,
  },
  {
    provide: CxUserProfileConnector,
    useClass: PyUserProfileConnector,
  },
  UserConsentConnector,
  {
    provide: CxUserConsentConnector,
    useClass: PyUserConsentConnector,
  },
  {
    provide: CxUserConsentService,
    useExisting: PyUserConsentService,
  },
];

@NgModule({
  imports: [UserStoreModule, UserEventModule],
  providers: [...rootProviders, ...cxProviders, ...facadeProviders],
})
export class UserBaseModule {}
